import { ToastrHelper, showToast } from '$lib/helpers/toastrHelper';
import { fetchWithAuth } from '$lib/utils/http/httpUtil';
import { type ErrorResponse } from '$lib/models/types';
import type { GetAbortDeleteListResponse } from '$lib/models/alii-data-sync/types';
import { extractErrorMessage } from '$lib/utils/error/errorUtils';


function createDataSyncStore() {
    const type: string = 'Data Sync';
    const baseUrl: string = '/api/data-sync';

    return {
        fetchAbortedEntities: async (): Promise<GetAbortDeleteListResponse | ErrorResponse> => {
            const url: string = `${baseUrl}/delete-aborted/client-list`;

            const response = await fetchWithAuth(url, {
                method: 'GET',
            });

            if (response.status == 204) {
                return {
                    message: ToastrHelper.notFound(type),
                    detail: ToastrHelper.notFound(type), // Add detail field for Problem Details compatibility
                };
            }

            if (!response.ok) {
                const errorMessage = await extractErrorMessage(response, `Failed to fetch aborted entities (HTTP ${response.status})`);
                showToast({
                    title: 'Error',
                    message: errorMessage,
                    type: 'error'
                });
                return [];
            }

            try {
                return await response.json();
            } catch (error) {
                console.error('Error parsing aborted list response:', error);
                showToast({
                    title: 'Error',
                    message: 'Failed to load aborted list',
                    type: 'error'
                });
                return [];
            }
        },

        fetchAbortedClientEntities: async (id: number): Promise<GetAbortDeleteListResponse | ErrorResponse> => {
            const url: string = `${baseUrl}/delete-aborted/client-list/${id}`;

            const response = await fetchWithAuth(url, {
                method: 'GET',
            });

            if (response.status == 204) {
                return {
                    message: ToastrHelper.notFound(type),
                    detail: ToastrHelper.notFound(type), // Add detail field for Problem Details compatibility
                };
            }

            if (!response.ok) {
                const errorMessage = await extractErrorMessage(response, `Failed to fetch aborted entities (HTTP ${response.status})`);
                showToast({
                    title: 'Error',
                    message: errorMessage,
                    type: 'error'
                });
                return [];
            }

            try {
                return await response.json();
            } catch (error) {
                console.error('Error parsing clientlist response:', error);
                showToast({
                    title: 'Error',
                    message: 'Failed to load clientlist data',
                    type: 'error'
                });
                return [];
            }
        },

        download: async (clientId: number, entityId: number, syncType: string): Promise<void | ErrorResponse> => {
            const url: string = `${baseUrl}/delete-aborted/elements-to-delete-csv/${clientId}/${entityId}/${syncType}`;

            const response = await fetchWithAuth(url, {
                method: 'GET',
            });

            if (!response.ok) {
                const errorMessage = await extractErrorMessage(response, `Failed to download sync (HTTP ${response.status})`);
                showToast({
                    title: 'Error',
                    message: errorMessage,
                    type: 'error'
                });
                return { message: errorMessage };
            }

            try {
              const blob = await response.blob();

              const downloadUrl = window.URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = downloadUrl;
              const disposition = response.headers.get('Content-Disposition');
              let fileName = disposition?.split('filename=')[1].split(';')[0];
              if (!fileName) {
                  fileName = `${syncType}_${new Date().toISOString().split('T')[0]}.csv`;
              }
              link.download = fileName;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              window.URL.revokeObjectURL(downloadUrl);
            } catch (error) {
                console.error('Error parsing download response:', error);
                showToast({
                    title: 'Error',
                    message: 'Failed to download sync',
                    type: 'error'
                });
                return { message: 'Failed to download sync' };
            }
        },

        pause: async (clientId: number, entityId: number, syncType: string): Promise<void | ErrorResponse> => {
            const url: string = `${baseUrl}/delete-aborted/pause/${clientId}/${entityId}/${syncType}`;

            const response = await fetchWithAuth(url, {
                method: 'POST',
            });

            if (!response.ok) {
                const errorMessage = await extractErrorMessage(response, `Failed to pause sync (HTTP ${response.status})`);
                showToast({
                    title: 'Error',
                    message: errorMessage,
                    type: 'error'
                });
                return { message: errorMessage };
            }

            try {
                return;
            } catch (error) {
                console.error('Error parsing pause response:', error);
                showToast({
                    title: 'Error',
                    message: 'Failed to pause sync',
                    type: 'error'
                });
                return { message: 'Failed to pause sync' };
            }
        },


        force: async (clientId: number, entityId: number, syncType: string): Promise<void | ErrorResponse> => {
            const url: string = `${baseUrl}/delete-aborted/force/${clientId}/${entityId}/${syncType}`;

            const response = await fetchWithAuth(url, {
                method: 'POST',
            });

            if (!response.ok) {
                const errorMessage = await extractErrorMessage(response, `Failed to force sync (HTTP ${response.status})`);
                showToast({
                    title: 'Error',
                    message: errorMessage,
                    type: 'error'
                });
                return { message: errorMessage };
            }

            try {
                return;
            } catch (error) {
                console.error('Error parsing force response:', error);
                showToast({
                    title: 'Error',
                    message: 'Failed to force sync',
                    type: 'error'
                });
                return { message: 'Failed to force sync' };
            }
        }


    }
}

export const dataSyncStore = createDataSyncStore();